import { useState } from 'react';
import type { AnnotationEntry } from '@geosoft/viewer-sdk';
import { useViewerStore } from '@/state/useViewerStore';
import {
  buildGeoJsonExport,
  buildKmlExport,
  downloadTextFile,
} from './exportPlugin';

interface ExportPanelProps {
  readonly visibleLayerIds: ReadonlyArray<string>;
  readonly time: Date | null;
  readonly annotations: ReadonlyArray<AnnotationEntry>;
  readonly measurePoints: ReadonlyArray<readonly [number, number]>;
}

export function ExportPanel({
  visibleLayerIds,
  time,
  annotations,
  measurePoints,
}: ExportPanelProps) {
  const { readOnly } = useViewerStore();
  
  const [selectedFormat, setSelectedFormat] = useState<string>('image');
  const [selectedSize, setSelectedSize] = useState<string>('current');
  const [isExporting, setIsExporting] = useState(false);

  const exportFormats = [
    { id: 'image', label: 'PNG Image', icon: 'image', available: true },
    { id: 'pdf', label: 'PDF Report', icon: 'description', available: true },
    { id: 'geotiff', label: 'GeoTIFF', icon: 'landscape', available: true },
    { id: 'geojson', label: 'GeoJSON', icon: 'dataset', available: true },
    { id: 'kml', label: 'KML', icon: 'public', available: true },
    { id: 'video', label: 'Video', icon: 'videocam', available: false, reason: 'Heavy format - use GeoConverter' },
    { id: 'gltf', label: '3D Model (glTF)', icon: 'view_in_ar', available: true },
    { id: 'dxf', label: 'DXF', icon: 'architecture', available: true },
    { id: 'las', label: 'Point Cloud (LAS)', icon: 'grain', available: false, reason: 'Heavy format - use GeoConverter' }
  ];

  const sizePresets = [
    { id: 'current', label: 'Current View', width: 'auto', height: 'auto' },
    { id: 'hd', label: '1920×1080 (HD)', width: 1920, height: 1080 },
    { id: '4k', label: '3840×2160 (4K)', width: 3840, height: 2160 },
    { id: 'print', label: 'Print (300 DPI)', width: 'print', height: 'print' }
  ];

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      // Simulate export API call - in real implementation this would call BE-124
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // For now, fall back to existing export functions for some formats
      switch (selectedFormat) {
        case 'image': {
          const canvas = document.querySelector<HTMLCanvasElement>('.maplibregl-canvas');
          if (canvas) {
            const link = document.createElement('a');
            link.download = `geoviewer-export-${Date.now()}.png`;
            link.href = canvas.toDataURL('image/png');
            document.body.appendChild(link);
            link.click();
            link.remove();
          } else {
            alert('Map canvas not found. Ensure a MapLibre map is mounted.');
          }
          break;
        }
        case 'geojson':
          downloadTextFile(
            'geoviewer-export.geojson',
            JSON.stringify(buildGeoJsonExport(annotations), null, 2),
            'application/geo+json',
          );
          break;
        case 'kml':
          downloadTextFile(
            'geoviewer-export.kml', 
            buildKmlExport(annotations), 
            'application/vnd.google-earth.kml+xml'
          );
          break;
        default:
          alert(`${selectedFormat.toUpperCase()} export is not available yet.`);
      }
    } catch {
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div className="export-modal-content">
        <div className="export-section">
          <h4>
            <span className="material-symbols-outlined">image</span>
            Format
          </h4>
          <div className="format-grid" role="radiogroup" aria-label="Export format">
            {exportFormats.map((format) => (
              <button
                key={format.id}
                type="button"
                className={`format-option ${selectedFormat === format.id ? 'selected' : ''} ${!format.available ? 'unavailable' : ''}`}
                onClick={() => format.available && setSelectedFormat(format.id)}
                disabled={!format.available}
                role="radio"
                aria-checked={selectedFormat === format.id}
                title={format.available ? format.label : format.reason}
              >
                <span className="material-symbols-outlined">{format.icon}</span>
                <span className="format-label">{format.label}</span>
                {!format.available && (
                  <span className="material-symbols-outlined unavailable-icon">lock</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="export-section">
          <h4>
            <span className="material-symbols-outlined">aspect_ratio</span>
            Size
          </h4>
          <div className="size-options" role="radiogroup" aria-label="Export size">
            {sizePresets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`size-option ${selectedSize === preset.id ? 'selected' : ''}`}
                onClick={() => setSelectedSize(preset.id)}
                role="radio"
                aria-checked={selectedSize === preset.id}
              >
                <span className="size-label">{preset.label}</span>
                {preset.width !== 'auto' && preset.width !== 'print' && (
                  <span className="size-dimensions">{preset.width}×{preset.height}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="export-section">
          <h4>
            <span className="material-symbols-outlined">send</span>
            Options
          </h4>
          <div className="export-options">
            <label className="checkbox-option">
              <input type="checkbox" defaultChecked />
              Include visible layers ({visibleLayerIds.length})
            </label>
            <label className="checkbox-option">
              <input type="checkbox" defaultChecked={annotations.length > 0} disabled={annotations.length === 0} />
              Include annotations ({annotations.length})
            </label>
            <label className="checkbox-option">
              <input type="checkbox" defaultChecked={measurePoints.length > 0} disabled={measurePoints.length === 0} />
              Include measurements ({measurePoints.length})
            </label>
            <label className="checkbox-option">
              <input type="checkbox" defaultChecked={!!time} disabled={!time} />
              Include time state {time ? `(${time.toLocaleDateString()})` : '(none)'}
            </label>
          </div>
        </div>

        <div className="export-actions">
          <button
            type="button"
            className="secondary"
            disabled={isExporting}
          >
            <span className="material-symbols-outlined">close</span>
            Cancel
          </button>
          <button
            type="button"
            className="primary"
            onClick={handleExport}
            disabled={isExporting || (readOnly && !['image', 'pdf'].includes(selectedFormat))}
          >
            <span className="material-symbols-outlined">
              {isExporting ? 'hourglass_empty' : 'ios_share'}
            </span>
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
        </div>

        {readOnly && !['image', 'pdf'].includes(selectedFormat) && (
          <div className="export-readonly-notice">
            <span className="material-symbols-outlined">info</span>
            Read-only mode: Only image and PDF export allowed
          </div>
        )}

        <div className="export-heavy-format-notice">
          <p>
            <span className="material-symbols-outlined">info</span>
            For video, large point clouds, or batch processing, use{' '}
            <button type="button" className="link-button">
              <span className="material-symbols-outlined">open_in_new</span>
              GeoConverter
            </button>
          </p>
        </div>
      </div>
    </>
  );
}
